/**
 * The raw OriTech ramps. This file is the palette — change it and the whole system moves.
 *
 * Every ramp was generated in OKLCH from the anchors read off the logo, so the perceptual step
 * between 400 and 500 is the same size in moss as it is in copper. That is what makes
 * `moss-600` and `copper-600` usable as a matched pair rather than a coincidence.
 *
 * Nothing outside `semantic/themes.ts` should import from here. Components reference roles
 * ("the action colour"), not ramp steps ("copper 600"), because a role survives a re-theme and a
 * ramp step does not.
 */

export type Shade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
export type Ramp = Readonly<Record<Shade, string>>;

/**
 * Moss — the logo's green mark. Brand identity and structure: headers, active navigation,
 * selected rows, the shell. **Not** the primary tap target; see `copper`.
 * Anchors 400/600/800 are lifted straight from the logo and are exact.
 */
export const moss: Ramp = {
  50: '#F7FBEA',
  100: '#E8EDD6',
  200: '#CAD3B2',
  300: '#ADB990',
  400: '#8FA06F', // logo anchor — light moss
  500: '#748758',
  600: '#5A6E42', // logo anchor — the green mark itself
  700: '#4C5E38',
  800: '#3F4E2E', // logo anchor — deep moss
  900: '#263219',
};

/**
 * Copper — the logo's bronze arc, and the primary *action* colour. Warmer and more urgent than
 * moss, and unmistakably not a status colour, which is the whole reason it carries the buttons.
 * Anchors 300/500/700 are exact.
 */
export const copper: Ramp = {
  50: '#FFF7EE',
  100: '#FFE4C7',
  200: '#EBC39A',
  300: '#D9A06B', // logo anchor — light copper
  400: '#C78853',
  500: '#B5713C', // logo anchor — the bronze arc itself
  600: '#9F6132',
  700: '#8A5228', // logo anchor — deep copper
  800: '#693912',
  900: '#4A2100',
};

/**
 * Neutral — warm-tinted the whole way down, never pure grey. The cream is brand, not background
 * filler, and a neutral ramp that falls to #808080 in the middle would drain it out of every
 * surface, border and caption in the product.
 *
 * 900 is `#2C2A25` rather than the flat `#2B2B2B` in the brief: the same lightness, but it keeps
 * a trace of warmth so body text belongs to the palette instead of sitting on top of it.
 */
export const neutral: Ramp = {
  50: '#FAF8F3',
  100: '#F2EAD9', // brand cream
  200: '#D5D0C6',
  300: '#BDB6A9',
  400: '#A59D91',
  500: '#8C857A', // the only neutral that clears 3:1 on both themes — see border.strong
  600: '#726D63',
  700: '#5A554D',
  800: '#423F39',
  900: '#2C2A25',
};

/**
 * Success — deliberately cooler than moss (161° against moss's 128° in OKLCH). Moss is the
 * brand and appears constantly; success is an event. If the two shared a hue, a salesperson
 * would learn to stop seeing the confirmation.
 */
export const success: Ramp = {
  50: '#E8FFF2',
  100: '#D5F6E4',
  200: '#B2E3C9',
  300: '#90CFAF',
  400: '#70BB96',
  500: '#56A580',
  600: '#3F8F6B',
  700: '#277151', // solid fills use 700, not 600 — 600 only reaches 3.92:1 against white
  800: '#105439',
  900: '#003823',
};

/** Danger — terracotta rather than fire-engine red, so a destructive action still belongs here. */
export const danger: Ramp = {
  50: '#FFF6F4',
  100: '#FFE5DF',
  200: '#FFC3B4',
  300: '#FF9D86',
  400: '#F17C61',
  500: '#DA6146',
  600: '#C0492F',
  700: '#9D331C',
  800: '#7B1F09',
  900: '#580E00',
};

/**
 * Warning — gold, pushed to 87° in OKLCH. The brief suggested `#D4923A`, which measured 14°
 * from copper: close enough that a warning chip beside a copper button read as one object under
 * shop lighting. 31° of separation is the smallest gap that survives a dim room and a cheap panel.
 */
export const warning: Ramp = {
  50: '#FFF8E8',
  100: '#FEEBC0',
  200: '#EED394',
  300: '#DDBB67',
  400: '#CDA330',
  500: '#B08807',
  600: '#916F04',
  700: '#725806',
  800: '#564100',
  900: '#3A2B01',
};

/**
 * Info — a dusty slate-blue. The only cool hue in the system, kept low-chroma so it reads as a
 * quiet aside rather than a fifth brand colour.
 */
export const info: Ramp = {
  50: '#F1FAFF',
  100: '#D9F0FB',
  200: '#B8D9E9',
  300: '#98C1D6',
  400: '#7AAAC1',
  500: '#6193AB',
  600: '#4A7C93',
  700: '#356277',
  800: '#224A5B',
  900: '#13323F',
};

/*
 * ── Shop-theme ramps ──────────────────────────────────────────────────────────
 *
 * A second shop is a different business with a different name over the door, and it should not
 * open the same green app the first one does. These three ramps exist only to be the `brand`
 * role for a shop that chose them (see `semantic/shopThemes.ts`) — they never carry a status,
 * never carry the action colour, and are not reachable from a component.
 *
 * Each is built the same way moss and copper are: a 600 dark enough to hold white text, a 400
 * light enough to hold `neutral-900` on the dark theme, and a 100/800 pair for quiet fills. Hue
 * is the only thing that changes between them, which is what lets a shop's colour be swapped
 * without re-checking anything but contrast — and `scripts/check-contrast.mts` does check every
 * one of these, in both themes, exactly as it checks the two originals.
 */

/** Sapphire — a clean, saturated blue. Reads as "different company", not "different status". */
export const sapphire: Ramp = {
  50: '#F1F6FE',
  100: '#DCE8FA',
  200: '#B6CDF0',
  300: '#8DAFE3',
  400: '#7099D8',
  500: '#4A79C0',
  600: '#2F5B9E',
  700: '#264A82',
  800: '#1D3A66',
  900: '#142744',
};

/** Plum — the one warm-cool hybrid. Distinct from both blue and the copper action colour. */
export const plum: Ramp = {
  50: '#FAF4FD',
  100: '#EFE0F7',
  200: '#DCC2EC',
  300: '#C4A0DD',
  400: '#AE86CC',
  500: '#9264B4',
  600: '#7A4D9C',
  700: '#653E82',
  800: '#503167',
  900: '#351E45',
};

/** Slate — deliberately quiet, for a shop that wants no colour opinion at all. */
export const slate: Ramp = {
  50: '#F4F7FA',
  100: '#E2E9F0',
  200: '#C3CFDC',
  300: '#9FB0C3',
  400: '#7F93AB',
  500: '#62778F',
  600: '#4C6076',
  700: '#3D4E60',
  800: '#303E4C',
  900: '#1F2933',
};

/**
 * The two page grounds. Neither is a ramp step: light is warmer than `neutral-50` so the cream
 * shows, and dark leans green rather than neutral so the shell still feels like the logo at
 * 2am in a lit shop.
 */
export const ground = {
  light: '#FAF6ED',
  dark: '#1C1E19',
} as const;

/**
 * Dark-mode surface tiers. Not ramp steps: dark surfaces need to separate by 4–8 points of
 * lightness to read as layers at all, which is finer than the ramp's spacing, and they keep the
 * green lean of `ground.dark` rather than drifting to the neutral ramp's brown.
 */
export const surfaceDark = {
  sunken: '#151713',
  base: '#1C1E19',
  raised: '#24261F',
  overlay: '#2C2E26',
} as const;

/** Pure values, used only where a role genuinely needs them (text on a copper fill). */
export const absolute = {
  white: '#FFFFFF',
  black: '#000000',
} as const;

export const ramps = {
  moss,
  copper,
  neutral,
  success,
  danger,
  warning,
  info,
  sapphire,
  plum,
  slate,
} as const;
export type RampName = keyof typeof ramps;
